import { useEffect, useMemo, useState } from 'react';
import { Check, Loader2, X } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import type { DateRange } from 'react-day-picker';
import 'react-day-picker/style.css';
import type { AvailabilityRequest } from '../types';
import { useUpdateRequest, useUserPlan, useRequests } from '../hooks/useApi';

type SiteTypeOption = 'any' | 'rv' | 'tent_rv' | 'tent' | 'other';
type ElectricOption = 'any' | 'electric' | 'non_electric';

const WEEKDAY_OPTIONS = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

const SITE_TYPE_OPTIONS: { value: SiteTypeOption; label: string }[] = [
  { value: 'any', label: 'Any' },
  { value: 'rv', label: 'RV Sites' },
  { value: 'tent_rv', label: 'Tent or RV' },
  { value: 'tent', label: 'Tents Only' },
  { value: 'other', label: 'Other' },
];

function parseArrivalDays(value: unknown): number[] {
  const arr = typeof value === 'string' ? (() => { try { return JSON.parse(value); } catch { return null; } })() : value;
  if (Array.isArray(arr)) {
    return arr.filter((v): v is number => typeof v === 'number');
  }
  return [];
}

function fromIso(iso: string): Date | undefined {
  if (!iso) return undefined;
  // Handle both date-only ("2026-03-20") and full ISO ("2026-03-20T00:00:00.000Z")
  const dateOnly = iso.slice(0, 10);
  const d = new Date(`${dateOnly}T00:00:00`);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

function formatDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

interface EditRequestModalProps {
  request: AvailabilityRequest;
  onClose: () => void;
}

export function EditRequestModal({ request, onClose }: EditRequestModalProps) {
  const updateRequest = useUpdateRequest();
  const { data: userPlan } = useUserPlan();
  const { data: allRequests } = useRequests();
  const today = useMemo(() => new Date(), []);

  // Initialize state from request
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => ({
    from: fromIso(request.startDate),
    to: fromIso(request.endDate),
  }));
  const [stayLength, setStayLength] = useState(request.stayLength);
  const [arrivalDays, setArrivalDays] = useState<number[]>(() =>
    parseArrivalDays(request.arrivalDays),
  );
  const [siteType, setSiteType] = useState<SiteTypeOption>(
    (request.siteType as SiteTypeOption) || 'any',
  );
  const [electric, setElectric] = useState<ElectricOption>(
    (request.electric as ElectricOption) || 'any',
  );
  const [water, setWater] = useState(request.water ?? false);
  const [sewer, setSewer] = useState(request.sewer ?? false);
  const [includeAdaSites, setIncludeAdaSites] = useState(
    request.includeAdaSites ?? false,
  );
  const [isActive, setIsActive] = useState(request.isActive);
  const [error, setError] = useState<string | null>(null);

  // Plan limit check: can the user activate this request?
  const activeCount = useMemo(() => {
    if (!allRequests) return 0;
    return allRequests.filter((r) => r.isActive && r.id !== request.id).length;
  }, [allRequests, request.id]);

  const atLimit =
    userPlan &&
    Number.isFinite(userPlan.maxActiveAlerts) &&
    activeCount >= userPlan.maxActiveAlerts &&
    userPlan.tier === 'free';

  const canActivate = !atLimit || request.isActive;

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const toggleArrivalDay = (day: number) => {
    setArrivalDays((current) =>
      current.includes(day)
        ? current.filter((v) => v !== day)
        : [...current, day].sort((a, b) => a - b),
    );
  };

  const dateStepValid =
    !!dateRange?.from &&
    !!dateRange?.to &&
    dateRange.from.getTime() < dateRange.to.getTime() &&
    Number.isFinite(stayLength) &&
    stayLength > 0;

  const handleSave = async () => {
    setError(null);

    if (!dateStepValid) {
      setError('Please select valid start and end dates with stay length >= 1');
      return;
    }

    try {
      await updateRequest.mutateAsync({
        id: request.id,
        data: {
          startDate: formatDate(dateRange!.from!),
          endDate: formatDate(dateRange!.to!),
          stayLength,
          arrivalDays,
          siteType,
          electric,
          water,
          sewer,
          includeAdaSites,
          isActive,
        },
      });
      onClose();
    } catch (err) {
      if (err instanceof Error && err.message.includes('404')) {
        onClose();
        return;
      }
      setError(err instanceof Error ? err.message : 'Failed to update request');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-xl shadow-xl p-6 mx-4">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900">Edit Availability Request</h2>
          {(request.placeName || request.facilityName) && (
            <p className="text-sm text-gray-600 mt-1">
              {[request.placeName, request.facilityName].filter(Boolean).join(' — ')}
            </p>
          )}
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="space-y-6">
          {/* Active toggle */}
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">Active</label>
            <button
              type="button"
              onClick={() => {
                if (!isActive && !canActivate) return;
                setIsActive((v) => !v);
              }}
              disabled={!isActive && !canActivate}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                isActive ? 'bg-emerald-600' : 'bg-gray-300'
              } ${!isActive && !canActivate ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isActive ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          {!isActive && atLimit && !request.isActive && userPlan && (
            <p className="text-xs text-amber-700">
              You've reached the free plan limit of {userPlan.maxActiveAlerts} active alert. Upgrade to reactivate.
            </p>
          )}

          {/* Date range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date range
            </label>
            {dateRange?.from && dateRange?.to && (
              <p className="text-sm text-gray-600 mb-2">
                {dateRange.from.toLocaleDateString()} &ndash; {dateRange.to.toLocaleDateString()}
              </p>
            )}
            <div className="flex justify-center overflow-x-auto">
              <DayPicker
                mode="range"
                selected={dateRange}
                onSelect={setDateRange}
                min={1}
                numberOfMonths={2}
                disabled={{ before: today }}
                defaultMonth={dateRange?.from ?? today}
                classNames={{ months: 'flex flex-nowrap gap-8' }}
              />
            </div>
          </div>

          {/* Stay length */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Length of stay (# nights)
            </label>
            <input
              type="number"
              min={1}
              max={30}
              value={stayLength}
              onChange={(e) => {
                const parsed = Number.parseInt(e.target.value, 10);
                setStayLength(Number.isFinite(parsed) ? parsed : 1);
              }}
              className="w-36 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
            />
          </div>

          {/* Arrival days */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Arrive on specific weekdays (optional)
            </label>
            <div className="flex flex-wrap gap-2">
              {WEEKDAY_OPTIONS.map((day) => {
                const active = arrivalDays.includes(day.value);
                return (
                  <button
                    key={day.value}
                    type="button"
                    onClick={() => toggleArrivalDay(day.value)}
                    className={`px-3 py-2 rounded-lg border text-sm transition-colors ${
                      active
                        ? 'bg-emerald-600 text-white border-emerald-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {day.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Site type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Site Type</label>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {SITE_TYPE_OPTIONS.map((option) => {
                const active = siteType === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setSiteType(option.value)}
                    className={`px-3 py-2 rounded-lg border text-sm ${
                      active
                        ? 'bg-emerald-50 border-emerald-500 text-emerald-700'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Electric */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Electric</label>
            <select
              value={electric}
              onChange={(e) => setElectric(e.target.value as ElectricOption)}
              className="w-56 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
            >
              <option value="any">No preference</option>
              <option value="electric">Electric</option>
              <option value="non_electric">Non-electric</option>
            </select>
          </div>

          {/* Checkboxes */}
          <div className="flex flex-wrap gap-4">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={water}
                onChange={(e) => setWater(e.target.checked)}
                className="w-4 h-4"
              />
              Water
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={sewer}
                onChange={(e) => setSewer(e.target.checked)}
                className="w-4 h-4"
              />
              Sewer
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={includeAdaSites}
                onChange={(e) => setIncludeAdaSites(e.target.checked)}
                className="w-4 h-4"
              />
              Include ADA Sites
            </label>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={updateRequest.isPending || !dateStepValid}
              className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {updateRequest.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
